import React, {
    memo,
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useInterval } from 'react-use';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import dayjs from 'dayjs';
import { LSTORAGE_KEY } from 'src/constants';
import { LocalStore, Logger } from 'src/utils';
import { CommonService } from 'src/services';
import { IAppConfig, IAppMenu, IAppTag } from 'src/services/AppService';
import { useOutsideClick } from 'src/hooks';
import {
    isMobileSelector,
    menuConfigSelector,
    pageSelector,
    tagConfigSelector,
} from 'src/selectors/app';
import { IconButton, Element, Container, RouterLink } from 'src/components';
import Navbar from 'src/components/navbar';
import NavbarExpand from './expand';

interface IProps {
    isSticky: boolean;
    config?: IAppConfig;
}

function buildClassMenu(pathName: string, path: any) {
    if (pathName === undefined || path === undefined) {
        return false;
    }
    return pathName === path;
}

function NavbarHeader({ isSticky, config }: IProps) {
    let menus = useSelector(menuConfigSelector);
    let tags = useSelector(tagConfigSelector);
    const page = useSelector(pageSelector);
    const isMobile = useSelector(isMobileSelector);

    const menuRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const btnRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const [isExpand, setIsExpand] = useState<boolean>(false);
    const [countNewContent, setCountNewContent] = useState<number>(0);
    const navbarRef = useRef<HTMLDivElement>(null);

    const router = useRouter();
    const getCountNewContent = () => {
        let lastestRead: number =
            (LocalStore.get(LSTORAGE_KEY.LATEST_READ) as number) || 0;
        Logger.debug('notify | ', lastestRead);

        const isValidTime = dayjs(lastestRead).diff(dayjs(), 'day') < 1;

        if (!isValidTime) {
            lastestRead = 0;
        }

        CommonService.getCountNewContent(lastestRead)
            .then((resp: any) => {
                const count = resp.data[0].value;
                setCountNewContent(count);
            })
            .catch((e) => {
                Logger.error(e.error_code, 'getCountNewContent | Fail ', e);
            });
    };
    useEffect(() => {
        setTimeout(getCountNewContent);
    }, [router]);

    useInterval(() => {
        getCountNewContent();
    }, 60000);

    const renderMenus = useCallback(() => {
        if (!menus) return [];

        return menus?.map((item: IAppMenu, index: number) => {
            const isActive = buildClassMenu(page.type, item?.pageType);
            const _class = twMerge(
                'relative',
                'overflow-hidden',
                'flex items-center',
                'text-[17px] uppercase',
                'leading-[normal]',
                'after:hidden',
                'after:h-[2px] after:bg-[rgba(255,255,255,0.6)]',
                'after:absolute after:bottom-0 after:left-[20px] after:right-[20px]',
                '[&:nth-child(1)_a]:w-[71px] [&:nth-child(1)]:ml-[-8px]',
                '[&:nth-child(2)_a]:w-[52px]',
                '[&:nth-child(3)_a]:w-[72px]',
                '[&:nth-child(4)_a]:w-[81px]',
                '[&_h2]:font-[400]',
                cn({
                    ['after:block [&_h2]:font-[900]']: isActive,
                }),
            );
            return (
                <Navbar.Item
                    active={isActive}
                    key={`navbar.item.${index}`}
                    className={_class}
                >
                    <Navbar.Link
                        classNameLink={cn(
                            'flex items-center justify-center',
                            '!text-white',
                            'h-[40px]',
                            'py-0',
                        )}
                        to={item.url}
                        as={item.url}
                    >
                        <Element renderAs="h2">{item.name}</Element>
                        {countNewContent > 0 && item.url === '/tin-moi.epi' && (
                            <Element
                                renderAs="span"
                                className={cn(
                                    'flex items-center justify-center',
                                    'w-[14px] h-[14px]',
                                    'absolute top-[4px] right-0',
                                    'text-[9px] font-[800]',
                                    'rounded-full',
                                    'bg-[#db562b]',
                                )}
                            >
                                {countNewContent}
                            </Element>
                        )}
                    </Navbar.Link>
                </Navbar.Item>
            );
        });
    }, [menus, router, countNewContent, page]);

    const renderTags = useCallback(() => {
        if (!tags) return [];

        return tags?.map((item: IAppTag, index: number) => (
            <Navbar.Item
                key={`navbar.item.${index}`}
                className={twMerge(
                    'h-[20px]',
                    'flex items-center',
                    'overflow-hidden',
                    'h-[20px]',
                    'text-[1.4rem] leading-[18px]',
                    'font-normal mx-[4px] my-0 px-[8px] py-0 rounded-[10px]',
                    'border border-solid border-[#ffffff4d]',
                )}
            >
                <RouterLink to={item.url} as={item.url} title={item.name}>
                    <Element renderAs="span">{item.name}</Element>
                </RouterLink>
            </Navbar.Item>
        ));
    }, [tags]);

    const handleClick = useCallback(
        (evt: React.MouseEvent) => {
            evt.preventDefault();
            evt.stopPropagation();
            setIsExpand(!isExpand);
        },
        [isExpand],
    );

    useOutsideClick([menuRef, btnRef], (isExpand) => {
        if (!isExpand) {
            setIsExpand(false);
        }
    });

    if (config?.tags) {
        tags = config?.tags;
    }
    if (config?.menus) {
        menus = config?.menus;
    }

    return (
        <Element
            className={twMerge(
                'navbar-header',
                'bm-topbar',
                'relative text-white',
                cn({
                    ['fixed top-0 left-0 right-0 z-[111]']: isSticky,
                }),
            )}
        >
            <Element>
                <Navbar
                    className={cn(
                        'h-[40px] bg-[var(--primary)]',
                        'overflow-hidden',
                    )}
                    domRef={navbarRef}
                >
                    <Container className="max-xs:p-0">
                        <Navbar.Menu
                            className={cn(
                                'flex items-center flex-wrap',
                                'text-[1.4rem] font-semibold',
                                'max-xs:pl-[20px] pr-[40px]',
                            )}
                        >
                            {renderMenus()}
                            {!isMobile && renderTags()}
                            <Navbar.Item
                                className={cn(
                                    'navbar-expand-button',
                                    'absolute top-0 right-[10px]',
                                    'xs:max-md:right-[10%]',
                                )}
                            >
                                <IconButton
                                    title="Chuyên mục"
                                    icon={
                                        isExpand
                                            ? 'new-x-close-regular'
                                            : 'new-bar-regular'
                                    }
                                    ref={btnRef}
                                    onClick={handleClick}
                                    className={cn(
                                        'flex items-center',
                                        'text-[20px]',
                                        'h-[40px]',
                                        'px-[10px]',
                                        'bg-[rgba(255,255,255,0.1)]',
                                        {
                                            ['bg-[var(--black-10)]']: isExpand,
                                        },
                                    )}
                                />
                            </Navbar.Item>
                        </Navbar.Menu>
                    </Container>
                </Navbar>
                <div ref={menuRef}>
                    <NavbarExpand
                        setIsExpand={setIsExpand}
                        isExpand={isExpand}
                        config={config}
                        navbarRef={navbarRef}
                    />
                </div>
            </Element>
        </Element>
    );
}

export default memo(NavbarHeader);
